import { useEffect, useMemo, useState } from 'react';

import { getConfig } from '@app/config';

import * as dailyRewardQuota from '@entities/tasks/utils/dailyRewardQuota';

import type { Task } from '@services/TasksService';

interface Defaults {
  maxVisible: number;
  maxPerDay: number;
}

interface TaskLimits {
  maxVisible: number;
  maxPerDay: number;
  limitReached: boolean;
  effectiveLimit: number;
  getAvailableTasksWithinLimit: (tasks: Task[] | undefined | null) => Task[];
  getAvailableCountWithinLimit: (tasks: Task[] | undefined | null) => number;
}

export const useTaskLimits = (
  defaults: Defaults = { maxVisible: 40, maxPerDay: 40 },
): TaskLimits => {
  const [maxVisible, setMaxVisible] = useState<number>(defaults.maxVisible);
  const [maxPerDay, setMaxPerDay] = useState<number>(defaults.maxPerDay);

  useEffect(() => {
    (async () => {
      const cfg = await getConfig();
      const cfgTasks = cfg.tasks || {};

      setMaxVisible(
        typeof cfgTasks.maxVisible === 'number'
          ? cfgTasks.maxVisible
          : defaults.maxVisible,
      );
      setMaxPerDay(
        typeof cfgTasks.maxPerDay === 'number'
          ? cfgTasks.maxPerDay
          : defaults.maxPerDay,
      );
    })();
  }, [defaults.maxPerDay, defaults.maxVisible]);

  const limitReached = useMemo(
    () => dailyRewardQuota.isLimitReached(maxPerDay),
    [maxPerDay],
  );

  const effectiveLimit = useMemo(() => {
    const remaining = dailyRewardQuota.getRemaining(maxPerDay);
    return Math.max(0, Math.min(maxVisible, remaining));
  }, [maxVisible, maxPerDay]);

  const filterAvailable = (tasks: Task[] | undefined | null): Task[] => {
    return (tasks ?? []).filter((t) => t.status === 'available');
  };

  const getAvailableTasksWithinLimit = (
    tasks: Task[] | undefined | null,
  ): Task[] => {
    const available = filterAvailable(tasks);
    return available.slice(0, effectiveLimit);
  };

  const getAvailableCountWithinLimit = (
    tasks: Task[] | undefined | null,
  ): number => {
    return getAvailableTasksWithinLimit(tasks).length;
  };

  return {
    maxVisible,
    maxPerDay,
    limitReached,
    effectiveLimit,
    getAvailableTasksWithinLimit,
    getAvailableCountWithinLimit,
  };
};
